import { withTimeout } from './capability.ts';
import type { StorageRequest } from './opfs.worker.ts';

/** Promise wrapper around the OPFS worker. One instance per page. */
export class LocalStorageFiles {
  private worker = new Worker(new URL('./opfs.worker.ts', import.meta.url), { type: 'module' });
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: any) => void; reject: (e: Error) => void }>();
  persistent = false;
  ready: Promise<void>;

  constructor() {
    this.worker.onmessage = (ev) => {
      const { id, ok, result, error } = ev.data;
      const p = this.pending.get(id);
      if (!p) return;
      this.pending.delete(id);
      ok ? p.resolve(result) : p.reject(new Error(error));
    };
    // If the storage worker never answers (failed to load, or the browser's file system hangs), carry on
    // recording to memory; the green room shows a warning.
    this.ready = withTimeout('local disk check', this.call<{ persistent: boolean }>({ op: 'init' }), 5000).then(
      (r) => void (this.persistent = r.persistent),
      () => void (this.persistent = false),
    );
    this.worker.onerror = (e) => console.error('[takehouse] storage worker failed', e.message);
  }

  private call<T>(req: StorageRequest, transfer: Transferable[] = []): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ id, req }, transfer);
    });
  }

  open = (name: string) => this.call<number>({ op: 'open', name });
  /** `data` is copied, so callers may reuse their buffer. */
  write(name: string, position: number, data: Uint8Array) {
    const copy = data.slice().buffer;
    return this.call<number>({ op: 'write', name, position, data: copy }, [copy]);
  }
  /** Replaces a small file in one go (and flushes it). */
  put(name: string, data: Uint8Array) {
    const copy = data.slice().buffer;
    return this.call<number>({ op: 'put', name, data: copy }, [copy]);
  }
  async readText(name: string) {
    const size = await this.size(name);
    return new TextDecoder().decode(await this.read(name, 0, size));
  }
    read = async (name: string, start: number, end: number) =>
    new Uint8Array(await this.call<ArrayBuffer>({ op: 'read', name, start, end }));
  size = (name: string) => this.call<number>({ op: 'size', name });
  flush = (name: string) => this.call<null>({ op: 'flush', name });
  close = (name: string) => this.call<null>({ op: 'close', name });
  delete = (name: string) => this.call<null>({ op: 'delete', name });
  list = () => this.call<{ name: string; size: number }[]>({ op: 'list' });
}

let instance: LocalStorageFiles | null = null;
export const localFiles = () => (instance ??= new LocalStorageFiles());

/** Free space the browser will let us use, plus a request to protect our data from eviction. */
export async function storageEstimate() {
  try {
    const persisted = (await navigator.storage.persisted?.()) || (await navigator.storage.persist?.()) || false;
    const { quota = 0, usage = 0 } = await navigator.storage.estimate();
    return { free: Math.max(0, quota - usage), quota, usage, persisted };
  } catch {
    return { free: Infinity, quota: Infinity, usage: 0, persisted: false };
  }
}
